import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const SCRIPT_ID = "recaptcha-script";

/** without a site key there is nothing to render, callers use this to decide
 * whether a solved captcha is required before submitting */
export const isRecaptchaEnabled = Boolean(SITE_KEY);

const loadScript = () =>
  new Promise((resolve) => {
    if (window.grecaptcha?.render) return resolve();

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    // api.js takes its own onload callback name, polling keeps this component
    // self contained and works when the script is already on its way in
    const timer = setInterval(() => {
      if (window.grecaptcha?.render) {
        clearInterval(timer);
        resolve();
      }
    }, 100);
  });

/** Google reCAPTCHA v2 checkbox. `onChange` receives the token when solved and
 * an empty string when it expires or errors. Parents can call `reset()` on the
 * ref once their form has been submitted. */
export const Recaptcha = forwardRef(({ onChange, error }, ref) => {
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  // kept in a ref so a new onChange identity cannot re-render the widget
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetId.current !== null) {
        window.grecaptcha?.reset(widgetId.current);
        onChangeRef.current?.("");
      }
    },
  }));

  useEffect(() => {
    if (!SITE_KEY) return;

    let cancelled = false;

    loadScript().then(() => {
      if (cancelled || !containerRef.current || widgetId.current !== null) {
        return;
      }

      widgetId.current = window.grecaptcha.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => onChangeRef.current?.(token),
        "expired-callback": () => onChangeRef.current?.(""),
        "error-callback": () => onChangeRef.current?.(""),
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!SITE_KEY) return null;

  return (
    <div className="mt-3">
      <div ref={containerRef} />
      {error ? <p className="font-xssss text-red mt-1 mb-0">{error}</p> : null}
    </div>
  );
});

export default Recaptcha;
